import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Camera, X, ClipboardCheck, Boxes, CheckCircle2, AlertTriangle, History } from 'lucide-react'
import { api } from '../lib/api'
import { Button } from '../components/ui/Button'
import { CameraScan } from '../components/CameraScan'
import { useActiveTenant } from '../contexts/tenant'

type PalletItem = { productId: string; sku: string | null; productName: string | null; quantityUnits: number; quantityBoxes: number }
type Conf = { id: string; palletCode: string | null; roundId: string | null; divergent: number; createdAt: string; byName: string | null; byEmail: string | null }
type RoundInfo = { id: string; startedAt: string; finishedAt: string | null; startedByName: string | null; conferred: number; divergent: number }
type Pallet = { id: string; code: string; status: string; locationCode: string | null; items: PalletItem[] }

/** Conferência de armazém PALETE A PALETE: bipa o QR do palete, conta 1 a 1 e
 *  compara com o que o sistema tem. Não altera estoque — divergências viram alerta. */
export function ConferenciaArmazemPage() {
  const tenantId = useActiveTenant()
  const [scanning, setScanning] = useState(false)
  const [manual, setManual] = useState('')
  const [pallet, setPallet] = useState<Pallet | null>(null)
  const [counts, setCounts] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const { data: historico = [], refetch: refetchHist } = useQuery<Conf[]>({
    queryKey: ['pallet-conferences', tenantId],
    queryFn: async () => (await api.get('/pallets/conferences', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })
  const { data: rounds = [], refetch: refetchRounds } = useQuery<RoundInfo[]>({
    queryKey: ['pallet-rounds-list', tenantId],
    queryFn: async () => (await api.get('/pallets/rounds', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  type Round = { active: boolean; total: number; conferred: number; pending: number; pendingList: { code: string; location: string | null }[] }
  const { data: round, refetch: refetchRound } = useQuery<Round>({
    queryKey: ['pallet-round', tenantId],
    queryFn: async () => (await api.get('/pallets/round/status', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })
  const [showPend, setShowPend] = useState(false)

  async function iniciarRodada() {
    if (!window.confirm('Iniciar uma rodada de conferência completa? Você terá que passar por TODOS os paletes do cliente.')) return
    setBusy(true); setMsg(null)
    try { await api.post('/pallets/round/start', { tenantId }); refetchRound() }
    catch (e: any) { setMsg({ ok: false, text: e?.response?.data?.title ?? 'Falha ao iniciar rodada' }) }
    finally { setBusy(false) }
  }
  async function finalizarRodada() {
    setBusy(true); setMsg(null)
    try {
      const { data: r } = await api.post('/pallets/round/finish', { tenantId })
      setMsg({ ok: true, text: `Rodada finalizada — ${r.total} palete(s) conferido(s).` })
      refetchRound(); refetchRounds()
    } catch (e: any) { setMsg({ ok: false, text: e?.response?.data?.title ?? 'Falha ao finalizar' }) }
    finally { setBusy(false) }
  }

  async function resolver(code: string) {
    const c = code.trim()
    if (!c || busy) return
    setBusy(true); setMsg(null)
    try {
      const { data } = await api.get('/pallets/by-code', { params: { tenantId, code: c } })
      setPallet(data)
      setCounts(Object.fromEntries((data.items ?? []).map((i: PalletItem) => [i.productId, ''])))
      setManual('')
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.status === 404 ? `Palete "${c}" não encontrado` : (e?.response?.data?.title ?? 'Falha ao buscar palete') })
    } finally { setBusy(false) }
  }

  async function conferir() {
    if (!pallet || busy) return
    setBusy(true); setMsg(null)
    try {
      const payload = { tenantId, counts: pallet.items.map(i => ({ productId: i.productId, countedBoxes: Number(counts[i.productId]) || 0 })) }
      const { data: r } = await api.post(`/pallets/${pallet.id}/conference`, payload)
      setMsg({
        ok: r.divergent === 0,
        text: r.divergent === 0 ? `Palete ${r.pallet} OK — bateu tudo.` : `Palete ${r.pallet}: ${r.divergent} divergência(s) — veja em Divergências (Armazém).`,
      })
      setPallet(null); setCounts({}); refetchHist(); refetchRound(); refetchRounds()
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.title ?? 'Falha ao conferir' })
    } finally { setBusy(false) }
  }

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="mx-auto max-w-md space-y-4 pb-24">
      <div>
        <h2 className="font-display text-xl text-gray-900">Conf. Armazém (por palete)</h2>
        <p className="text-xs text-gray-500">bipe o QR do palete · conte as <b>caixas</b> · compara com o sistema (não altera estoque)</p>
      </div>

      {msg && (
        <div className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm ${msg.ok ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
          {msg.ok ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <AlertTriangle className="h-5 w-5 shrink-0" />}
          {msg.text}
        </div>
      )}

      {/* Rodada de conferência completa (todos os paletes) */}
      {round && (round.active ? (
        <div className="space-y-2 rounded-xl border border-blue-200 bg-white p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-gray-700">Rodada em andamento</span>
            <span className="font-mono font-semibold text-gray-900">{round.conferred} / {round.total}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${round.total ? (round.conferred / round.total) * 100 : 0}%` }} />
          </div>
          <div className="flex items-center justify-between gap-2">
            <button onClick={() => setShowPend(v => !v)} className="text-xs text-blue-600 hover:underline disabled:opacity-50" disabled={round.pending === 0}>
              {round.pending === 0 ? 'todos conferidos ✓' : `${round.pending} pendente(s) — ${showPend ? 'ocultar' : 'ver'}`}
            </button>
            <Button size="sm" className="shrink-0 whitespace-nowrap" loading={busy} disabled={round.pending > 0} onClick={finalizarRodada}>
              Finalizar rodada
            </Button>
          </div>
          {showPend && round.pending > 0 && (
            <div className="max-h-48 space-y-1 overflow-auto rounded-lg bg-gray-50 p-2">
              {round.pendingList.map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="font-mono font-semibold text-gray-700">{p.code}</span>
                  <span className="text-gray-400">{p.location ?? 'sem local'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="mb-3 text-sm text-gray-600"><b>{round.total}</b> palete(s) no cliente. Inicie uma rodada pra conferir todos.</p>
          <Button className="w-full" loading={busy} onClick={iniciarRodada}>Iniciar rodada completa</Button>
        </div>
      ))}

      {!pallet ? (
        <div className="space-y-3">
          <Button size="lg" className="w-full" onClick={() => { setMsg(null); setScanning(true) }}>
            <Camera className="h-5 w-5" /> Bipar QR do palete
          </Button>
          <div className="flex gap-2">
            <input value={manual} onChange={e => setManual(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') resolver(manual) }}
              placeholder="ou digite o código do palete"
              className="h-12 flex-1 rounded-xl border border-gray-300 px-4 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50" />
            <Button variant="secondary" size="lg" loading={busy} disabled={!manual.trim()} onClick={() => resolver(manual)}>Buscar</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3 rounded-xl border border-blue-200 bg-blue-50/40 p-4">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600"><Boxes className="h-5 w-5" /></span>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-base font-bold text-gray-900">{pallet.code}</p>
              <p className="text-xs text-gray-500">{pallet.locationCode ?? 'sem localização'} · {pallet.status}</p>
            </div>
            <button onClick={() => { setPallet(null); setCounts({}) }} className="text-gray-400"><X className="h-5 w-5" /></button>
          </div>

          {pallet.items.length === 0 ? (
            <p className="rounded-lg bg-white px-3 py-2 text-sm text-gray-500">O sistema não tem itens neste palete. Se tiver mercadoria física, conte abaixo — vira divergência.</p>
          ) : (
            <div className="space-y-2">
              {pallet.items.map(i => (
                <div key={i.productId} className="rounded-lg bg-white p-3">
                  {/* Contagem cega: NÃO mostra a quantidade do sistema. */}
                  <p className="mb-0.5 font-mono text-sm font-semibold text-gray-800">{i.sku}</p>
                  <p className="mb-1 truncate text-xs text-gray-500">{i.productName}</p>
                  <input inputMode="numeric" value={counts[i.productId] ?? ''}
                    onChange={e => setCounts(c => ({ ...c, [i.productId]: e.target.value.replace(/[^\d]/g, '') }))}
                    placeholder="caixas contadas no palete"
                    className="h-12 w-full rounded-lg border border-gray-300 px-3 text-center text-lg font-semibold focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50" />
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Button size="lg" className="flex-1" loading={busy} onClick={conferir}>
              <ClipboardCheck className="h-5 w-5" /> Conferir palete
            </Button>
            <Button size="lg" variant="secondary" onClick={() => { setMsg(null); setScanning(true) }}>
              <Camera className="h-5 w-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Histórico AGRUPADO POR RODADA (onda) — início/fim, quem, e as conferências */}
      {historico.length > 0 && (() => {
        const byRound = new Map<string, Conf[]>()
        for (const c of historico) {
          const k = c.roundId ?? 'avulsas'
          if (!byRound.has(k)) byRound.set(k, [])
          byRound.get(k)!.push(c)
        }
        const dt = (s: string | null) => (s ? new Date(s).toLocaleString('pt-BR') : null)
        // Ordena: rodadas conhecidas (na ordem de `rounds`, mais recente primeiro) + avulsas no fim.
        const ordered = [...rounds.map(r => ({ round: r, confs: byRound.get(r.id) ?? [] })).filter(x => x.confs.length),
          ...(byRound.has('avulsas') ? [{ round: null as RoundInfo | null, confs: byRound.get('avulsas')! }] : [])]
        return (
          <div className="space-y-3">
            <p className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-gray-400">
              <History className="h-3.5 w-3.5" /> Histórico por onda de conferência
            </p>
            {ordered.map(({ round, confs }, gi) => (
              <details key={round?.id ?? 'avulsas'} open={gi === 0} className="rounded-xl border border-gray-200 bg-white">
                <summary className="cursor-pointer px-3 py-2 text-sm">
                  {round ? (
                    <span className="font-medium text-gray-800">
                      Onda de {dt(round.startedAt)}
                      {round.finishedAt ? <> — <span className="text-green-600">finalizada {dt(round.finishedAt)}</span></> : <span className="text-blue-600"> — em andamento</span>}
                    </span>
                  ) : <span className="font-medium text-gray-500">Conferências avulsas (sem onda)</span>}
                  <span className="ml-2 text-xs text-gray-400">
                    {confs.length} palete(s){round?.startedByName ? ` · iniciou: ${round.startedByName}` : ''}
                    {round && round.divergent > 0 ? ` · ${round.divergent} c/ diverg.` : ''}
                  </span>
                </summary>
                <div className="space-y-1 border-t border-gray-100 p-2">
                  {confs.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="font-mono font-semibold text-gray-800">{c.palletCode ?? '—'}</p>
                        <p className="truncate text-[11px] text-gray-400">{c.byName ?? c.byEmail ?? 'sistema'} · {new Date(c.createdAt).toLocaleString('pt-BR')}</p>
                      </div>
                      {c.divergent === 0
                        ? <span className="inline-flex shrink-0 items-center gap-1 text-green-600"><CheckCircle2 className="h-4 w-4" /> OK</span>
                        : <span className="inline-flex shrink-0 items-center gap-1 text-amber-600"><AlertTriangle className="h-4 w-4" /> {c.divergent} diverg.</span>}
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )
      })()}

      {scanning && (
        <CameraScan hint="Aponte para o QR do palete" onScan={(t) => { setScanning(false); resolver(t) }} onClose={() => setScanning(false)} />
      )}
    </div>
  )
}
